import { client, getToken } from "./client";

export type DataScienceJobOrigin = "data_science" | "api" | "api_ecs";

export interface DataScienceJob {
  id: number;
  job_name: string;
  job_type: string;
  origin?: DataScienceJobOrigin;
  organization_id: number;
  is_active: boolean;
  cron_expression?: string;
  last_execution?: string;
  next_execution?: string;
  execution_count: number;
  last_status?: string;
  last_error?: string;
  last_started_at?: string;
  last_completed_at?: string;
  last_duration_seconds?: number;
  parameters?: string;
  created_at: string;
  updated_at?: string;
}

export interface CreateJobRequest {
  job_name: string;
  job_type: string;
  organization_id: number;
  cron_expression?: string;
  parameters?: string;
}

export interface UpdateJobRequest {
  job_name?: string;
  is_active?: boolean;
  cron_expression?: string;
  parameters?: string;
}

export interface TaskResponse {
  task_id: string;
  status: string;
  message: string;
  created_at: string;
}

export interface TaskStatus {
  task_id: string;
  bright_id?: number;
  job_name?: string;
  job_type?: string;
  status: string;
  progress?: number;
  message: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  error?: string;
  result?: any;
  organization_id?: number;
  task_name?: string;
  brightId?: number;
  execution_id?: string;
  has_logs?: boolean;
  artifact_snapshot?: {
    source?: string;
    pipelineFilename?: string;
    modelFilename?: string;
    configFilename?: string;
  };
}

export class DataScienceJobApi {
  static async createJob(request: CreateJobRequest): Promise<DataScienceJob> {
    try {
      const response = await client.post(
        "/api/datascience-scheduler/jobs",
        request,
        {
          headers: {
            Authorization: getToken(),
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.error("Error creating data science job:", error);
      throw error;
    }
  }

  static async getJob(jobId: number): Promise<DataScienceJob> {
    try {
      const response = await client.get(
        `/api/datascience-scheduler/jobs/${jobId}`,
        {
          headers: {
            Authorization: getToken(),
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.error("Error fetching data science job:", error);
      throw error;
    }
  }

  static async updateJob(
    jobId: number,
    request: UpdateJobRequest
  ): Promise<DataScienceJob> {
    try {
      const response = await client.put(
        `/api/datascience-scheduler/jobs/${jobId}`,
        request,
        {
          headers: {
            Authorization: getToken(),
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.error("Error updating data science job:", error);
      throw error;
    }
  }

  static async deleteJob(jobId: number): Promise<void> {
    try {
      await client.delete(`/api/datascience-scheduler/jobs/${jobId}`, {
        headers: {
          Authorization: getToken(),
        },
      });
    } catch (error: any) {
      console.error("Error deleting data science job:", error);
      throw error;
    }
  }

  static async listJobs(params?: {
    organization_id?: number;
    job_type?: string;
    is_active?: boolean;
    page?: number;
    pageSize?: number;
  }): Promise<
    | DataScienceJob[]
    | {
        items?: DataScienceJob[];
        data?: DataScienceJob[];
        jobs?: DataScienceJob[];
        total: number;
        page: number;
        pages: number;
        size: number;
      }
  > {
    try {
      const queryParams = new URLSearchParams();
      if (params?.organization_id)
        queryParams.append(
          "organization_id",
          params.organization_id.toString()
        );
      if (params?.job_type) queryParams.append("job_type", params.job_type);
      if (params?.is_active !== undefined)
        queryParams.append("is_active", params.is_active.toString());
      if (params?.page) queryParams.append("page", params.page.toString());
      if (params?.pageSize)
        queryParams.append("pageSize", params.pageSize.toString());

      const url = `/api/datascience-scheduler/jobs${
        queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`;

      const response = await client.get(url, {
        headers: {
          Authorization: getToken(),
        },
      });
      return response.data;
    } catch (error: any) {
      console.error("Error listing data science jobs:", error);
      throw error;
    }
  }

  static async executeJob(jobId: number): Promise<TaskResponse> {
    try {
      const response = await client.post(
        `/api/datascience-scheduler/jobs/${jobId}/execute`,
        {},
        {
          headers: {
            Authorization: getToken(),
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.error("Error executing data science job:", error);
      throw error;
    }
  }

  static async startDemandForecast(request: {
    organization_id: number;
    forecast_periods?: number;
    starting_date?: string;
    split_by_sku?: boolean;
  }): Promise<TaskResponse> {
    throw new Error("Demand forecast direct start is no longer supported");
  }

  static async getTaskStatus(
    taskId: string,
    jobType: string
  ): Promise<TaskStatus> {
    throw new Error("Task status endpoint removed");
  }

  static async getJobNames(jobIds: number[]): Promise<Record<number, string>> {
    try {
      const response = await client.post(
        "/api/datascience-jobs/names",
        jobIds,
        {
          headers: {
            Authorization: getToken(),
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching job names:", error);
      throw error;
    }
  }

  static async listTasks(params?: {
    organization_id?: number;
    status?: string;
    job_type?: string;
    page?: number;
    pageSize?: number;
  }): Promise<
    | TaskStatus[]
    | {
        items?: TaskStatus[];
        data?: TaskStatus[];
        tasks?: TaskStatus[];
        total: number;
        page: number;
        pages: number;
        size: number;
      }
  > {
    try {
      const queryParams = new URLSearchParams();
      if (params?.job_type) queryParams.append("job_type", params.job_type);
      if (params?.organization_id != null)
        queryParams.append("organization_id", String(params.organization_id));
      if (params?.status) queryParams.append("status", params.status);
      if (params?.page) queryParams.append("page", String(params.page));
      if (params?.pageSize) queryParams.append("size", String(params.pageSize));

      const url = `/api/datascience-scheduler/tasks${
        queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`;

      const response = await client.get(url, {
        headers: {
          Authorization: getToken(),
        },
      });

      return response.data;
    } catch (error: any) {
      console.error("Error listing tasks:", error);
      throw error;
    }
  }

  static async checkHealth(): Promise<{
    status: string;
    service: string;
    error?: string;
  }> {
    try {
      const response = await client.get("/api/datascience-scheduler/health", {
        headers: {
          Authorization: getToken(),
        },
      });
      return response.data;
    } catch (error: any) {
      console.error("Error checking data science service health:", error);
      throw error;
    }
  }

  static async reconcileTask(taskId: string): Promise<string> {
    try {
      const response = await client.post(
        `/api/datascience-scheduler/tasks/${taskId}/reconcile`,
        {},
        {
          headers: {
            Authorization: getToken(),
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.error("Error reconciling task:", error);
      throw error;
    }
  }

  static async cancelTask(taskId: string): Promise<string> {
    try {
      const response = await client.post(
        `/api/datascience-scheduler/tasks/${taskId}/cancel`,
        {},
        {
          headers: {
            Authorization: getToken(),
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.error("Error cancelling task:", error);
      throw error;
    }
  }

  static async getTaskLogs(
    taskId: string,
    nextToken?: string,
    limit: number = 200
  ): Promise<{
    events: Array<{ timestamp: number; message: string }>;
    nextToken?: string;
  }> {
    try {
      const queryParams = new URLSearchParams();
      if (nextToken) queryParams.append("nextToken", nextToken);
      queryParams.append("limit", String(limit));

      const url = `/api/datascience-scheduler/tasks/${taskId}/logs${
        queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`;

      const response = await client.get(url, {
        headers: {
          Authorization: getToken(),
        },
      });
      return response.data;
    } catch (error: any) {
      console.error("Error getting task logs:", error);
      throw error;
    }
  }

  static async downloadTaskLogs(taskId: string) {
    try {
      const response = await client.get(
        `/api/datascience-scheduler/tasks/${taskId}/logs/download`,
        {
          headers: {
            Authorization: getToken(),
          },
          responseType: "blob",
        }
      );
      return response;
    } catch (error: any) {
      console.error("Error downloading task logs:", error);
      throw error;
    }
  }
}
